import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import FormList from './pages/admin/FormList';
import FormBuilder from './pages/admin/FormBuilder';
import ReviewCriteria from './pages/admin/ReviewCriteria';
import ApplicationList from './pages/admin/ApplicationList';
import ApplicationDetail from './pages/admin/ApplicationDetail';
import ReviewerManagement from './pages/admin/ReviewerManagement';
import AssignmentManager from './pages/admin/AssignmentManager';
import DecisionManager from './pages/admin/DecisionManager';
import EmailTemplates from './pages/admin/EmailTemplates';
import SMTPSettings from './pages/admin/SMTPSettings';
import RegistrationList from './pages/admin/RegistrationList';
import AuditLog from './pages/admin/AuditLog';
import ShortURLs from './pages/admin/ShortURLs';
import ResetPassword from './pages/ResetPassword';
import CountrySettings from './pages/admin/CountrySettings';
import Dashboards from './pages/admin/Dashboards';
import Selection from './pages/admin/Selection';
import FacilitatorDashboard from './pages/facilitator/FacilitatorDashboard';
import ViewerLayout from './pages/viewer/ViewerLayout';
import ViewerDashboard from './pages/viewer/ViewerDashboard';
import ViewerApplications from './pages/viewer/ViewerApplications';
import ProfileSettings from './pages/admin/ProfileSettings';
import ReviewerLayout from './pages/reviewer/ReviewerLayout';
import ReviewerDashboard from './pages/reviewer/ReviewerDashboard';
import ReviewApplication from './pages/reviewer/ReviewApplication';
import ScholarshipForm from './pages/public/ScholarshipForm';
import RegistrationForm from './pages/public/RegistrationForm';

var theme = createTheme({
  palette: {
    primary: { main: '#1a5276', light: '#2980b9', dark: '#0e3a53' },
    secondary: { main: '#e67e22', light: '#f39c12', dark: '#d35400' },
    background: { default: '#f5f6fa', paper: '#ffffff' },
    success: { main: '#27ae60' },
    error: { main: '#c0392b' },
    warning: { main: '#f39c12' },
  },
  typography: {
    fontFamily: 'Inter, Roboto, Helvetica, sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
  },
});

function ProtectedRoute(props) {
  var auth = useAuth();
  if (!auth.user && auth.loading) {
    return React.createElement(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 8 } },
      React.createElement(CircularProgress));
  }
  if (!auth.user) {
    return React.createElement(Navigate, { to: '/login', replace: true });
  }
  if (props.allowedRoles && props.allowedRoles.indexOf(auth.user.role) === -1) {
    return React.createElement(Navigate, { to: '/', replace: true });
  }
  return React.createElement(Outlet);
}

function SmartRedirect() {
  var auth = useAuth();
  if (auth.user) {
    if (auth.user.role === 'admin') return React.createElement(Navigate, {to:'/admin',replace:true});
    if (auth.user.role === 'viewer') return React.createElement(Navigate, {to:'/viewer',replace:true});
    if (auth.user.role === 'facilitator') return React.createElement(Navigate, {to:'/facilitator',replace:true});
    return React.createElement(Navigate, {to:'/reviewer',replace:true});
  }
  return React.createElement(Navigate, {to:'/login',replace:true});
}

function App() {
  var E = React.createElement;
  var R = Route;
  return E(ThemeProvider, { theme: theme },
    E(CssBaseline),
    E(AuthProvider, null,
      E(BrowserRouter, null,
        E(Routes, null,
          E(R, { path: '/login', element: E(Login) }),
          E(R, { path: '/accept-invite/:token', element: E(AcceptInvite) }),
          E(R, { path: '/reset-password/:token', element: E(ResetPassword) }),
          E(R, { path: '/apply/:formId', element: E(ScholarshipForm) }),
          E(R, { path: '/register/:formId', element: E(RegistrationForm) }),
          E(R, { element: E(ProtectedRoute, { allowedRoles: ['admin'] }) },
            E(R, { element: E(AdminLayout) },
              E(R, { path: '/admin', element: E(Dashboard) }),
              E(R, { path: '/admin/forms', element: E(FormList) }),
              E(R, { path: '/admin/forms/:id/builder', element: E(FormBuilder) }),
              E(R, { path: '/admin/forms/:id/criteria', element: E(ReviewCriteria) }),
              E(R, { path: '/admin/forms/:id/applications', element: E(ApplicationList) }),
              E(R, { path: '/admin/forms/:id/applications/:appId', element: E(ApplicationDetail) }),
              E(R, { path: '/admin/forms/:id/assignments', element: E(AssignmentManager) }),
              E(R, { path: '/admin/forms/:id/decisions', element: E(DecisionManager) }),
              E(R, { path: '/admin/forms/:id/registrations', element: E(RegistrationList) }),
              E(R, { path: '/admin/reviewers', element: E(ReviewerManagement) }),
              E(R, { path: '/admin/email-templates', element: E(EmailTemplates) }),
              E(R, { path: '/admin/smtp', element: E(SMTPSettings) }),
              E(R, { path: '/admin/audit-log', element: E(AuditLog) }),
              E(R, { path: '/admin/short-urls', element: E(ShortURLs) }),
              E(R, { path: '/admin/countries', element: E(CountrySettings) }),
              E(R, { path: '/admin/dashboards', element: E(Dashboards) }),
              E(R, { path: '/admin/selection', element: E(Selection) }),
              E(R, { path: '/admin/settings', element: E(ProfileSettings) })
            )
          ),
          E(R, { element: E(ProtectedRoute, { allowedRoles: ['reviewer', 'facilitator', 'admin'] }) },
            E(R, { element: E(ReviewerLayout) },
              E(R, { path: '/reviewer', element: E(ReviewerDashboard) }),
              E(R, { path: '/reviewer/review/:assignmentId', element: E(ReviewApplication) })
            )
          ),
          E(R, { element: E(ProtectedRoute, { allowedRoles: ['facilitator', 'admin'] }) },
            E(R, { path: '/facilitator', element: E(FacilitatorDashboard) })
          ),
          E(R, { element: E(ProtectedRoute, { allowedRoles: ['viewer', 'admin'] }) },
            E(R, { element: E(ViewerLayout) },
              E(R, { path: '/viewer', element: E(ViewerDashboard) }),
              E(R, { path: '/viewer/dashboards', element: E(ViewerDashboard) }),
              E(R, { path: '/viewer/applications', element: E(ViewerApplications) })
            )
          ),
          E(R, { path: '/', element: E(SmartRedirect) }),
          E(R, { path: '*', element: E(SmartRedirect) })
        )
      )
    )
  );
}

export default App;
